/**
 * Airline Baggage Allowance Database
 * Based on official airline policies (2024)
 * 
 * Structure:
 * - airlineCode: IATA airline code (e.g., 'BA' for British Airways)
 * - airlineName: Full airline name
 * - classes: Object with economy, business, first class allowances
 *   - carryOn: { pieces, weight, dimensions }
 *   - checked: { pieces, weight, dimensions }
 */

export const airlineBaggageDatabase = {
  // British Airways - Verified 2024 policies
  'BA': {
    airlineName: 'British Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '23kg', dimensions: '56x45x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '90x75x43cm' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '23kg', dimensions: '56x45x25cm' },
        checked: { pieces: 2, weight: '23kg each', dimensions: '90x75x43cm each' }
      },
      business: {
        carryOn: { pieces: 2, weight: '23kg each', dimensions: '56x45x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '90x75x43cm each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '23kg each', dimensions: '56x45x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '90x75x43cm each' }
      }
    }
  },
  
  // Royal Air Maroc
  'AT': {
    airlineName: 'Royal Air Maroc',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '12kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '15kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Ethiopian Airlines
  'ET': {
    airlineName: 'Ethiopian Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Air France - Verified 2024 policies
  'AF': {
    airlineName: 'Air France',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '12kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '12kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Lufthansa - Verified 2024 policies
  'LH': {
    airlineName: 'Lufthansa',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Emirates - Verified 2024 policies
  'EK': {
    airlineName: 'Emirates',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x38x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '150cm total' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x38x20cm' },
        checked: { pieces: 1, weight: '25kg', dimensions: '150cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x38x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '150cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x38x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '150cm total each' }
      }
    }
  },
  
  // Qatar Airways - Verified 2024 policies
  'QR': {
    airlineName: 'Qatar Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '50x37x25cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '50x37x25cm' },
        checked: { pieces: 1, weight: '25kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '50x37x25cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '50x37x25cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Turkish Airlines - Verified 2024 policies
  'TK': {
    airlineName: 'Turkish Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '25kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // KLM - Verified 2024 policies
  'KL': {
    airlineName: 'KLM',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '12kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '12kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Virgin Atlantic
  'VS': {
    airlineName: 'Virgin Atlantic',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '12kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '12kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // American Airlines
  'AA': {
    airlineName: 'American Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '18kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Delta
  'DL': {
    airlineName: 'Delta',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '18kg', dimensions: '56x35x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '56x35x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '56x35x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // United Airlines
  'UA': {
    airlineName: 'United Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '18kg', dimensions: '56x35x22cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '56x35x22cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '18kg each', dimensions: '56x35x22cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Air Canada
  'AC': {
    airlineName: 'Air Canada',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Swiss International Air Lines
  'LX': {
    airlineName: 'Swiss International Air Lines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Scandinavian Airlines
  'SK': {
    airlineName: 'Scandinavian Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Aer Lingus
  'EI': {
    airlineName: 'Aer Lingus',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x24cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x24cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x24cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // EgyptAir
  'MS': {
    airlineName: 'EgyptAir',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // RwandAir
  'WB': {
    airlineName: 'RwandAir',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Air Malta
  'KM': {
    airlineName: 'Air Malta',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Air Transat
  'TS': {
    airlineName: 'Air Transat',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Brussels Airlines
  'SN': {
    airlineName: 'Brussels Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Iberia
  'IB': {
    airlineName: 'Iberia',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '56x45x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '56x45x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '56x45x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Austrian Airlines
  'OS': {
    airlineName: 'Austrian Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // TAP Air Portugal
  'TP': {
    airlineName: 'TAP Air Portugal',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // LOT Polish Airlines
  'LO': {
    airlineName: 'LOT Polish Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Saudia
  'SV': {
    airlineName: 'Saudia',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Etihad Airways
  'EY': {
    airlineName: 'Etihad Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '50x40x21cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '50x40x21cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '50x40x21cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Gulf Air
  'GF': {
    airlineName: 'Gulf Air',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Royal Jordanian
  'RJ': {
    airlineName: 'Royal Jordanian',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // South African Airways
  'SA': {
    airlineName: 'South African Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Kenya Airways
  'KQ': {
    airlineName: 'Kenya Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // SriLankan Airlines
  'UL': {
    airlineName: 'SriLankan Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Thai Airways
  'TG': {
    airlineName: 'Thai Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x45x25cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x45x25cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x45x25cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Singapore Airlines
  'SQ': {
    airlineName: 'Singapore Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Cathay Pacific
  'CX': {
    airlineName: 'Cathay Pacific',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Japan Airlines
  'JL': {
    airlineName: 'Japan Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 3, weight: '45kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // All Nippon Airways
  'NH': {
    airlineName: 'All Nippon Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 3, weight: '45kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Korean Air
  'KE': {
    airlineName: 'Korean Air',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '45kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Asiana Airlines
  'OZ': {
    airlineName: 'Asiana Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '45kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // China Airlines
  'CI': {
    airlineName: 'China Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // EVA Air
  'BR': {
    airlineName: 'EVA Air',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Qantas
  'QF': {
    airlineName: 'Qantas',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Air New Zealand
  'NZ': {
    airlineName: 'Air New Zealand',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x40x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // LATAM Airlines
  'LA': {
    airlineName: 'LATAM Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Avianca
  'AV': {
    airlineName: 'Avianca',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Copa Airlines
  'CM': {
    airlineName: 'Copa Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Aeromexico
  'AM': {
    airlineName: 'Aeromexico',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Nigerian Airlines
  'P4': {
    airlineName: 'Air Peace',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  'W3': {
    airlineName: 'Arik Air',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  'QI': {
    airlineName: 'Ibom Air',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Other African Airlines
  'TU': {
    airlineName: 'Tunisair',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  'AH': {
    airlineName: 'Air Algérie',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Middle Eastern Airlines
  'KU': {
    airlineName: 'Kuwait Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  'ME': {
    airlineName: 'Middle East Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Asian Airlines
  'MF': {
    airlineName: 'Xiamen Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '5kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  'MU': {
    airlineName: 'China Eastern',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '5kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  'CA': {
    airlineName: 'Air China',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '5kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  'CZ': {
    airlineName: 'China Southern',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '5kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '5kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Aeroflot
  'SU': {
    airlineName: 'Aeroflot',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x40x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x40x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Jet Airways
  '9W': {
    airlineName: 'Jet Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '15kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // SpiceJet
  'SG': {
    airlineName: 'SpiceJet',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '15kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // IndiGo
  '6E': {
    airlineName: 'IndiGo',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '15kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Jetstar
  'JQ': {
    airlineName: 'Jetstar',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Virgin Australia
  'VA': {
    airlineName: 'Virgin Australia',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '56x36x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '56x36x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // LATAM Brasil
  'JJ': {
    airlineName: 'LATAM Brasil',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Gol Transportes Aéreos
  'G3': {
    airlineName: 'Gol Transportes Aéreos',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '10kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '10kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Aerolíneas Argentinas
  'AR': {
    airlineName: 'Aerolíneas Argentinas',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x35x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x35x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // El Al
  'LY': {
    airlineName: 'El Al',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '56x40x25cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '56x40x25cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '56x40x25cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Pegasus Airlines
  'PC': {
    airlineName: 'Pegasus Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '8kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '30kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '8kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '40kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Flydubai
  'FZ': {
    airlineName: 'Flydubai',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x38x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '150cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x38x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '150cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x38x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '150cm total each' }
      }
    }
  },
  
  // Oman Air
  'WY': {
    airlineName: 'Oman Air',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Air Arabia
  'G9': {
    airlineName: 'Air Arabia',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // TAAG Angola Airlines
  'DT': {
    airlineName: 'TAAG Angola Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // TACV Cabo Verde Airlines
  'TA': {
    airlineName: 'TACV Cabo Verde Airlines',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Jazeera Airways
  'J9': {
    airlineName: 'Jazeera Airways',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x20cm' },
        checked: { pieces: 1, weight: '20kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 2, weight: '40kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x20cm each' },
        checked: { pieces: 3, weight: '50kg each', dimensions: '158cm total each' }
      }
    }
  },
  
  // Default/Generic airline (fallback)
  'DEFAULT': {
    airlineName: 'Airline',
    classes: {
      economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      premium_economy: {
        carryOn: { pieces: 1, weight: '7kg', dimensions: '55x40x23cm' },
        checked: { pieces: 1, weight: '23kg', dimensions: '158cm total' }
      },
      business: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 2, weight: '32kg each', dimensions: '158cm total each' }
      },
      first: {
        carryOn: { pieces: 2, weight: '7kg each', dimensions: '55x40x23cm each' },
        checked: { pieces: 3, weight: '32kg each', dimensions: '158cm total each' }
      }
    }
  }
};

/**
 * Get baggage allowance for a specific airline and class
 * @param {string} airlineCode - IATA airline code (e.g., 'BA', 'AT')
 * @param {string} travelClass - Travel class ('economy', 'business', 'first')
 * @returns {Object} Baggage allowance object
 */
export const getBaggageAllowance = (airlineCode, travelClass = 'economy') => {
  // Normalize class name
  const normalizedClass = travelClass.toLowerCase().replace(/_/g, '');
  const classMap = {
    'economy': 'economy',
    'business': 'business',
    'first': 'first',
    'firstclass': 'first',
    'premiumeconomy': 'premium_economy',
    'premiumeconomyclass': 'premium_economy'
  };
  
  // Try to use the exact class first, then mapped class, then fallback
  let mappedClass = classMap[normalizedClass] || travelClass.toLowerCase();
  
  // Get airline data
  const airline = airlineBaggageDatabase[airlineCode] || airlineBaggageDatabase['DEFAULT'];
  
  // Get class-specific baggage - try premium_economy first, then mapped class, then economy
  let baggage = airline.classes[mappedClass];
  if (!baggage && mappedClass === 'premium_economy') {
    // If premium_economy not available, fallback to economy
    baggage = airline.classes.economy;
  }
  if (!baggage) {
    baggage = airline.classes[mappedClass] || airline.classes.economy;
  }
  
  return {
    airlineName: airline.airlineName,
    class: mappedClass,
    carryOn: baggage.carryOn,
    checked: baggage.checked
  };
};

/**
 * Format baggage information for display
 * @param {Object} baggage - Baggage allowance object
 * @returns {Object} Formatted baggage strings
 */
export const formatBaggageForDisplay = (baggage) => {
  if (!baggage) {
    console.log('⚠️ formatBaggageForDisplay: baggage is null/undefined');
    return null;
  }
  
  console.log('🔍 formatBaggageForDisplay input:', {
    baggage: baggage,
    carryOn: baggage.carryOn,
    checked: baggage.checked,
    carryOnType: typeof baggage.carryOn,
    checkedType: typeof baggage.checked
  });
  
  const carryOnText = baggage.carryOn 
    ? `${baggage.carryOn.pieces} piece${baggage.carryOn.pieces > 1 ? 's' : ''} (${baggage.carryOn.weight})`
    : 'Not included';
    
  // Format checked baggage - handle weight that already includes "each"
  let checkedText = 'Not included';
  if (baggage.checked) {
    const weight = baggage.checked.weight || '';
    const alreadyHasEach = weight.includes('each');
    checkedText = `${baggage.checked.pieces} piece${baggage.checked.pieces > 1 ? 's' : ''} (${weight}${!alreadyHasEach && baggage.checked.pieces > 1 ? ' each' : ''})`;
  }
  
  console.log('✅ formatBaggageForDisplay output:', {
    carryOnText: carryOnText,
    checkedText: checkedText
  });
  
  return {
    carryOn: carryOnText,
    checked: checkedText,
    carryOnDetails: baggage.carryOn,
    checkedDetails: baggage.checked
  };
};
